'use client'
import { useState } from 'react'
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useTransform,
  type MotionValue,
} from 'framer-motion'

type ProofChartProps = {
  // Shared 0→1 timeline owned by the section — drives the whole chart in sync
  // with the step timeline.
  progress: MotionValue<number>
  labels: {
    cost: string
    impressions: string
    timeline: string
    down: string
    up: string
  }
  fa: boolean
}

// Geometry lives in a 720×300 viewBox. The chart is conceptual — it carries no
// numeric values, so it shows the SHAPE of the outcome (cost falls, impressions
// rise over a campaign) without claiming specific figures.
// Plot area: y-axis at x=72, x-axis at y=264.
const COST_LINE = 'M72,82 C200,96 290,150 378,166 C480,185 590,214 684,224'
const IMPRESSIONS_LINE = 'M72,230 C200,220 290,176 378,158 C480,136 590,92 684,68'
const X_TICKS = [72, 225, 378, 531, 684]

const COST = '#f0a850' // amber — the "cost" metric, falling
const IMPR = '#00b4d8' // brand cyan — the "impressions" metric, rising

export default function ProofChart({ progress, labels, fa }: ProofChartProps) {
  const reduce = useReducedMotion()

  // Everything derives from the shared progress value.
  const axisOpacity = useTransform(progress, [0, 0.12], [0, 1])
  const dotOpacity = useTransform(progress, [0.9, 1], [0, 1])
  const playheadX = useTransform(progress, [0, 1], [72, 684])
  const playheadOpacity = useTransform(progress, [0, 0.08, 0.92, 1], [0, 1, 1, 0])

  // The endpoint "live" ring only appears once the draw completes.
  const [done, setDone] = useState(() => progress.get() >= 0.97)
  useMotionValueEvent(progress, 'change', (v) => setDone(v >= 0.97))

  const legend = [
    { name: labels.cost, trend: labels.down, color: COST, arrow: '↓' },
    { name: labels.impressions, trend: labels.up, color: IMPR, arrow: '↑' },
  ]

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Metric data grid — identifies the two lines */}
      <div className="grid grid-cols-2 gap-2.5">
        {legend.map((it) => (
          <div
            key={it.name}
            className="rounded-xl border border-white/10 px-3 py-2.5 flex items-center gap-2.5"
            style={{ background: 'rgba(255,255,255,0.03)' }}
          >
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ background: it.color }}
            />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-white leading-tight truncate">{it.name}</p>
              <p
                className="text-[10px] font-semibold leading-tight mt-0.5"
                style={{ color: it.color }}
              >
                <span aria-hidden>{it.arrow}</span> {it.trend}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Chart — fills the middle, vertically centered */}
      <div className="flex-1 flex items-center">
        <svg
          viewBox="0 0 720 300"
          className="w-full h-auto"
          role="img"
          aria-label={`${labels.cost} down, ${labels.impressions} up`}
        >
          <defs>
            <linearGradient id="proof-impr" x1="0" y1="1" x2="1" y2="0">
              <stop offset="0" stopColor="#0335b7" />
              <stop offset="1" stopColor="#00b4d8" />
            </linearGradient>
            <filter id="proof-glow" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="6" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Axes, gridlines and ticks — fade in as the draw starts */}
          <motion.g style={{ opacity: axisOpacity }}>
            {[96, 156, 216].map((y) => (
              <line
                key={y}
                x1="72"
                y1={y}
                x2="684"
                y2={y}
                stroke="rgba(255,255,255,0.05)"
                strokeWidth="1"
              />
            ))}

            {/* Y-axis + up arrowhead */}
            <line
              x1="72"
              y1="36"
              x2="72"
              y2="266"
              stroke="rgba(255,255,255,0.28)"
              strokeWidth="1.5"
            />
            <path d="M66,46 L72,33 L78,46 Z" fill="rgba(255,255,255,0.32)" />

            {/* X-axis + right arrowhead */}
            <line
              x1="70"
              y1="264"
              x2="698"
              y2="264"
              stroke="rgba(255,255,255,0.28)"
              strokeWidth="1.5"
            />
            <path d="M688,257 L701,264 L688,271 Z" fill="rgba(255,255,255,0.32)" />

            {/* X-axis ticks */}
            {X_TICKS.map((x) => (
              <line
                key={x}
                x1={x}
                y1="264"
                x2={x}
                y2="270"
                stroke="rgba(255,255,255,0.22)"
                strokeWidth="1.5"
              />
            ))}

            {/* Origin dot */}
            <circle cx="72" cy="264" r="3" fill="rgba(255,255,255,0.3)" />
          </motion.g>

          {/* Cost line — falls; draws in lockstep with progress */}
          <motion.path
            d={COST_LINE}
            fill="none"
            stroke={COST}
            strokeWidth="3.5"
            strokeLinecap="round"
            style={{ pathLength: progress }}
          />

          {/* Impressions line — rises (the hero line, with glow) */}
          <motion.path
            d={IMPRESSIONS_LINE}
            fill="none"
            stroke="url(#proof-impr)"
            strokeWidth="4"
            strokeLinecap="round"
            filter="url(#proof-glow)"
            style={{ pathLength: progress }}
          />

          {/* Playhead — sweeps across in time with the step timeline */}
          <motion.g style={{ x: playheadX, opacity: playheadOpacity }}>
            <line
              x1="0"
              y1="48"
              x2="0"
              y2="264"
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="1.5"
              strokeDasharray="3 6"
            />
            <circle cx="0" cy="264" r="4" fill={IMPR} />
          </motion.g>

          {/* Pulsing "live" ring on the impressions endpoint — once the draw ends */}
          {done && !reduce && (
            <motion.circle
              cx="684"
              cy="68"
              r="7"
              fill="none"
              stroke={IMPR}
              strokeWidth="2"
              style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
              initial={{ opacity: 0, scale: 1 }}
              animate={{ opacity: [0.55, 0], scale: [1, 2.6] }}
              transition={{ duration: 1.9, repeat: Infinity, ease: 'easeOut' }}
            />
          )}

          {/* Endpoint dots */}
          <motion.circle cx="684" cy="224" r="6" fill={COST} style={{ opacity: dotOpacity }} />
          <motion.circle
            cx="684"
            cy="68"
            r="7"
            fill={IMPR}
            filter="url(#proof-glow)"
            style={{ opacity: dotOpacity }}
          />
        </svg>
      </div>

      {/* X-axis label */}
      <p
        className="text-center text-[10px] sm:text-xs"
        style={{ color: 'rgba(255,255,255,0.4)' }}
        dir={fa ? 'rtl' : 'ltr'}
      >
        {labels.timeline}
      </p>
    </div>
  )
}
